import { useEffect, useMemo, useState } from 'react';
import {
  getMyInvites,
  getPlans,
  getSubscription,
  skipOnboardingStep,
  type Plan,
} from '../../api/endpoints.js';
import { useAction, useApi } from '../../api/hooks.js';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../flow/stores.js';
import { canSubscribe } from '../../flow/entitlement.js';
import { goStep } from '../../flow/nav.js';
import { Failure, Loading } from '../../ui/kit.js';
import { FlowScreen, FlowSub, FlowTitle, PrimaryButton } from '../../ui/flow.js';

/**
 * Mobile screens 9–10: the plan step, and the reassurance screen behind "Pick
 * a plan later".
 *
 * THE WEB DOES NOT SELL (spec 2026-09-07 §10). Play Billing and StoreKit are
 * in-app only, so a browser cannot complete a purchase at all — this screen
 * shows what the plans cost and points at the apps. Coverage does not wait for
 * it: every account is granted the free plan at signup (0023), so both buttons
 * lead to the same next step, and an already-covered payer short-circuits past
 * on mount exactly as before.
 */


function priceOf(plan: Plan): string {
  if (plan.price_usd === null) return plan.billing_interval === 'year' ? '— / year' : '— / month';
  return `$${String(plan.price_usd)} / ${plan.billing_interval}`;
}

export function PlansStep() {
  const [tab, setTab] = useState<'single' | 'household'>('single');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const plans = useApi(getPlans, []);
  const subscription = useApi(getSubscription, []);
  // A pending invitation is a seat someone already pays for — the paywall must
  // say so before this person buys a plan of their own. Matched to the session
  // (phone / verified email); a read failure just means no banner.
  const myInvites = useApi(getMyInvites, []);
  const pendingInvite = (myInvites.data?.invites ?? [])[0] ?? null;

  // COVERED MEANS PAYING, and it is not the same as "has a live subscription".
  // Every account is granted the free plan at signup (0023) with
  // status 'active', so `status !== null && ACTIVE.has(status)` — what stood
  // here until 2026-08-31 — was true for every brand-new user, and the effect
  // below then redirected them straight past this step as a returning payer.
  // Nobody on the free plan could reach a checkout at all.
  const covered = !canSubscribe(subscription.data?.subscription);

  // Already covered (a returning payer): move on. There is no payment to wait
  // for here any more, so nothing polls and nothing celebrates.
  useEffect(() => {
    if (covered) goStep('profile', { replace: true });
  }, [covered]);

  // "Pick a plan later" is a SERVER skip now. It used to be a localStorage flag,
  // which made it both fragile and dangerous: it died on sign-out, and because
  // the blob was not keyed to an account, the next person to sign in on this
  // browser inherited it and walked past the paywall.
  const skipPlan = useAction(() => skipOnboardingStep('plan'), () => goStep('skip-plan'));

  const visible = useMemo(
    () => (plans.data?.plans ?? []).filter((plan) => plan.tier === tab),
    [plans.data, tab],
  );
  const selected =
    visible.find((plan) => plan.plan_code === selectedCode) ??
    visible.find((plan) => plan.billing_interval === 'year') ??
    visible[0];

  const yearly = visible.find((plan) => plan.billing_interval === 'year');
  const monthly = visible.find((plan) => plan.billing_interval === 'month');
  const savings =
    yearly?.price_usd != null && monthly?.price_usd != null && monthly.price_usd > 0
      ? Math.round((1 - yearly.price_usd / (monthly.price_usd * 12)) * 100)
      : null;

  if (plans.data === null || subscription.data === null) {
    return (
      <FlowScreen progress={60} onBack={() => window.history.back()}>
        {plans.error !== null && <Failure error={plans.error} />}
        {subscription.error !== null && <Failure error={subscription.error} />}
        {plans.error === null && subscription.error === null && <Loading />}
      </FlowScreen>
    );
  }

  return (
    <FlowScreen
      progress={60}
      onBack={() => window.history.back()}
      onClose={() => goStep('profile', { replace: true })}
      footer={
        <>
          <PrimaryButton onClick={() => goStep('profile')}>Continue</PrimaryButton>
          <button
            type="button"
            className="flow-link"
            onClick={() => skipPlan.run()}
            disabled={skipPlan.pending}
          >
            {skipPlan.pending ? 'Saving…' : 'Pick a plan later'}
          </button>
          {skipPlan.error !== null && <p className="flow-error">{skipPlan.error.message}</p>}
        </>
      }
    >
      <FlowTitle>Our subscription plans</FlowTitle>
      <FlowSub>
        You are already covered and being scanned on the free plan. A paid plan is what unlocks
        your reports — and it is bought in the app.
      </FlowSub>

      {pendingInvite !== null && (
        <div className="score-card">
          <p className="score-card-label">You have a household invitation</p>
          <p className="flow-sub" style={{ margin: '0 0 10px' }}>
            {`${pendingInvite.invited_by.first_name ?? ''} ${pendingInvite.invited_by.last_name ?? ''}`.trim() ||
              'Someone'}{' '}
            invited you to their household plan — accepting gives you a covered seat without buying
            a plan of your own. You can come back here any time.
          </p>
          <a className="flow-link" href="#/invites">
            Review the invitation →
          </a>
        </div>
      )}

      <div className="plan-toggle" role="tablist">
        {(['single', 'household'] as const).map((tier) => (
          <button
            key={tier}
            type="button"
            role="tab"
            aria-selected={tab === tier}
            className={tab === tier ? 'plan-tab active' : 'plan-tab'}
            onClick={() => {
              // Switching tabs preserves the monthly/annual choice.
              const interval = selected?.billing_interval;
              setTab(tier);
              const counterpart = (plans.data?.plans ?? []).find(
                (plan) => plan.tier === tier && plan.billing_interval === interval,
              );
              setSelectedCode(counterpart?.plan_code ?? null);
            }}
          >
            {tier === 'single' ? 'Individual' : 'Household'}
          </button>
        ))}
      </div>

      <div className="option-stack">
        {visible.map((plan) => {
          const isSelected = selected?.plan_code === plan.plan_code;
          return (
            <button
              key={plan.plan_code}
              type="button"
              className={isSelected ? 'plan-card selected' : 'plan-card'}
              onClick={() => setSelectedCode(plan.plan_code)}
              aria-pressed={isSelected}
            >
              <span className="plan-card-name">
                {plan.billing_interval === 'year' ? 'Annual' : 'Monthly'}
                {plan.billing_interval === 'year' && savings !== null && savings > 0 && (
                  <span className={isSelected ? 'save-badge selected' : 'save-badge'}>
                    SAVE {savings}%
                  </span>
                )}
              </span>
              <span className="plan-card-price">{priceOf(plan)}</span>
              <span className={isSelected ? 'plan-radio selected' : 'plan-radio'} aria-hidden="true">
                {isSelected && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4.5 12.5l5 5 10-11"
                      stroke="#fff"
                      strokeWidth="3.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
            </button>
          );
        })}
        {visible.length === 0 && <p className="flow-note">No plans of this tier are available.</p>}
      </div>

      <div className="score-card">
        <p className="score-card-label">Benefits</p>
        {[
          '24/7 real-time likeness monitoring',
          'Likeness detection notifications',
          'Priority email support',
          tab === 'household'
            ? `Protection for up to ${String(selected?.seat_limit ?? 5)} family members`
            : 'Protection for 1 person',
        ].map((benefit) => (
          <p key={benefit} className="benefit-row">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4.5 12.5l5 5 10-11"
                stroke="#380E99"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {benefit}
          </p>
        ))}
      </div>

      <div className="score-card">
        <p className="score-card-label">Subscribing happens in the app</p>
        <p className="flow-sub" style={{ margin: '0 0 10px' }}>
          Payment is handled by the App Store and Google Play, so a plan is purchased in the
          ImageShield app rather than here.
        </p>
        <a className="flow-link" href={APP_STORE_URL} target="_blank" rel="noreferrer noopener">
          App Store →
        </a>
        <br />
        <a className="flow-link" href={PLAY_STORE_URL} target="_blank" rel="noreferrer noopener">
          Google Play →
        </a>
      </div>

      <p className="flow-note" style={{ textAlign: 'center' }}>
        <a href="https://www.imageshield.com/privacy-policy" target="_blank" rel="noreferrer noopener">
          Privacy Policy
        </a>{' '}
        ·{' '}
        <a href="https://imageshield.com/terms-and-conditions" target="_blank" rel="noreferrer noopener">
          Terms and Conditions
        </a>
      </p>
    </FlowScreen>
  );
}

export function SkipPlanStep() {
  return (
    <FlowScreen
      progress={64}
      onBack={() => window.history.back()}
      footer={<PrimaryButton onClick={() => goStep('profile')}>Continue</PrimaryButton>}
    >
      <FlowTitle>
        No worries, you can upgrade to a paid plan at any point after completing your onboarding.
      </FlowTitle>
    </FlowScreen>
  );
}
